import { useCallback, useEffect, useRef, useState } from "react";

import { getEnvironmentBranding } from "../config";
import {
  currentBuildMetadata,
  fetchLatestBuildMetadata,
  isNewerBuild,
  RELEASE_UPDATE_CHECK_INTERVAL_MS,
} from "../releaseUpdate/buildMetadata";

const useReleaseUpdateDetector = ({
  checkIntervalMs = RELEASE_UPDATE_CHECK_INTERVAL_MS,
  currentBuild = currentBuildMetadata(),
  disabled = getEnvironmentBranding().name === "local",
  documentObject = typeof document === "undefined" ? null : document,
  fetchLatestBuildFunction = fetchLatestBuildMetadata,
  windowObject = typeof window === "undefined" ? null : window,
} = {}) => {
  const currentBuildRef = useRef(currentBuild);
  const activeRequestRef = useRef(null);
  const mountedRef = useRef(true);
  const [latestBuild, setLatestBuild] = useState(null);

  const checkForUpdate = useCallback(async () => {
    if (disabled || activeRequestRef.current) return null;

    const controller = new AbortController();
    activeRequestRef.current = controller;

    try {
      const latest = await fetchLatestBuildFunction({
        signal: controller.signal,
      });
      if (
        mountedRef.current &&
        isNewerBuild({
          currentBuild: currentBuildRef.current,
          latestBuild: latest,
        })
      ) {
        setLatestBuild(latest);
      }
      return latest;
    } catch {
      return null;
    } finally {
      if (activeRequestRef.current === controller) {
        activeRequestRef.current = null;
      }
    }
  }, [disabled, fetchLatestBuildFunction]);

  useEffect(() => {
    mountedRef.current = true;
    if (disabled || !documentObject || !windowObject) return undefined;

    checkForUpdate();
    const intervalId = windowObject.setInterval(
      checkForUpdate,
      checkIntervalMs,
    );
    const checkWhenVisible = () => {
      if (documentObject.visibilityState === "visible") {
        checkForUpdate();
      }
    };
    documentObject.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      mountedRef.current = false;
      activeRequestRef.current?.abort();
      activeRequestRef.current = null;
      windowObject.clearInterval(intervalId);
      documentObject.removeEventListener(
        "visibilitychange",
        checkWhenVisible,
      );
    };
  }, [
    checkForUpdate,
    checkIntervalMs,
    disabled,
    documentObject,
    windowObject,
  ]);

  return {
    checkForUpdate,
    latestBuild,
    updateAvailable: Boolean(latestBuild),
  };
};

export default useReleaseUpdateDetector;
